// import external modules
import React, { lazy, Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Switch } from "react-router-dom";

import Spinner from "../components/spinner/spinner";
import ErrorLayoutRoute from "../layouts/routes/errorRoutes";
import FullPageLayout from "../layouts/routes/fullpageRoutes";
// import internal(own) modules
import MainLayoutRoutes from "../layouts/routes/mainRoutes";

import getCompany from "../services/getCompany";
import getPix from "../services/getPix";
import { getServiceFieldFromCompany } from "../utility/support";
import { addContributor } from "../redux/actions/contributor";
import { setCompany } from "../redux/actions/company";
import { setInvoices } from "../redux/actions/invoices";

// Full Layout
const LazyLogin = lazy(() => import("../views/Login"));
const LazyForgotPassword = lazy(() => import("../views/ForgotPassword"));

// Main Layout
const LazyDashboard = lazy(() => import("../views/Dashboard"));

// Contributors
const LazyContributors = lazy(() => import("../views/Contributors"));
const LazyAddContributor = lazy(() => import("../views/AddContributor"));
const LazyAddContributorInBulk = lazy(() =>
  import("../views/AddContributorInBulk/wizard")
);
const LazyEditContributor = lazy(() => import("../views/EditContributor"));

// Company
const LazyCompany = lazy(() => import("../views/Company"));

// Cost Center
const LazyCostCenter = lazy(() => import("../views/CostCenter"));
const LazyAddCostCenter = lazy(() => import("../views/AddCostCenter"));
const LazyEditCostCenter = lazy(() => import("../views/EditCostCenter"));

// Relatórios
const LazyReports = lazy(() => import("../views/Reports/TravelsReport"));
const LazyCostCentersReport = lazy(() =>
  import("../views/Reports/CostCentersReport")
);
const LazyContributorsReport = lazy(() =>
  import("../views/Reports/ContributorsReport")
);

// Ayo entregas
const LazyNewOrder = lazy(() => import("../views/NewOrder"));
const LazyTrackOrders = lazy(() => import("../views/TrackOrders"));
const LazyHistoryOrder = lazy(() => import("../views/OrderHistory"));
const LazyOrderDetails = lazy(() => import("../views/OrderDetails"));
const LazyFinancialHistory = lazy(() => import("../views/FinancialHistory"));
const LazyPaymentHistory = lazy(() => import("../views/PaymentHistory"));
// const LazyInvoices = lazy(() => import('../views/Invoices'));
const LazyCanceledOrders = lazy(() => import("../views/CanceledOrders"));
const LazySettings = lazy(() => import("../views/Settings"));
const LazyScheduledOrders = lazy(() => import("../views/ScheduledOrders"));
const LazyDeliveryReports = lazy(() => import("../views/DeliveryReports"));
const LazyWallet = lazy(() => import("../views/Wallet"));

// Error Pages
const LazyErrorPage = lazy(() => import("../views/pages/error"));

function Router() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.contributor);
  const { id } = useSelector((state) => state.company);

  useEffect(() => {
    async function updateUserAndStore() {
      try {
        if (user) {
          user.empresas = [];

          for (const perfilEmpresa of user.perfisEmpresa) {
            const company = await getCompany(perfilEmpresa.split("_")[1]);
            user.empresas.push(company);
          }

          const service = getServiceFieldFromCompany(user.empresas[0]);
          user.servicoSelecionado = service;
          dispatch(addContributor(user));
          dispatch(setCompany(user.empresas[0]));
        }
      } catch (error) {
        console.error(error);
      }
    }

    updateUserAndStore();
  }, []);

  useEffect(() => {
    async function getInvoices() {
      const result = await getPix(id);

      dispatch(setInvoices(result));
    }

    getInvoices();
  }, [id, window.performance]);

  return (
    // Set the directory path if you are deplying in sub-folder
    <BrowserRouter basename="/">
      <Switch>
        <FullPageLayout
          exact
          path="/"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyLogin {...matchprops} />
            </Suspense>
          )}
        />

        <FullPageLayout
          exact
          path="/forgot-password"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyForgotPassword {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/company"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyCompany {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/dashboard"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyDashboard {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/contributors"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyContributors {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/contributors/edit/:id"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyEditContributor {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/addContributor"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyAddContributor {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/addContributorInBulk"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyAddContributorInBulk {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/costCenter"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyCostCenter {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/addCostCenter"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyAddCostCenter {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/costCenter/edit/:id"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyEditCostCenter {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/reports/travelsReport"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyReports {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/reports/costCentersReport"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyCostCentersReport {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/reports/contributorsReport"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyContributorsReport {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/new-order"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyNewOrder {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/wallet"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyWallet {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/track-orders"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyTrackOrders {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/order-history"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyHistoryOrder {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/canceled-orders"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyCanceledOrders {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/scheduled-orders"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyScheduledOrders {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/order-details/:orderId"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyOrderDetails {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/financial-history"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyFinancialHistory {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/payment-history"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyPaymentHistory {...matchprops} />
            </Suspense>
          )}
        />
        {/*<MainLayoutRoutes
          path="/invoices"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyInvoices {...matchprops} />
            </Suspense>
          )}
        />*/}

        <MainLayoutRoutes
          exact
          path="/settings"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazySettings {...matchprops} />
            </Suspense>
          )}
        />

        <MainLayoutRoutes
          exact
          path="/delivery-reports"
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyDeliveryReports {...matchprops} />
            </Suspense>
          )}
        />

        <ErrorLayoutRoute
          render={(matchprops) => (
            <Suspense fallback={<Spinner />}>
              <LazyErrorPage {...matchprops} />
            </Suspense>
          )}
        />
      </Switch>
    </BrowserRouter>
  );
}

export default Router;
